import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Ticket } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { Spinner } from "@baluu/ui/components/spinner";
import { CheckoutLoginGate } from "@baluu/ui/components/checkout";
import { PersonalDetailsForm } from "@baluu/ui/components/checkout/personal-details-form";
import { MandatoryAcceptanceCheckbox } from "@baluu/ui/components/checkout/mandatory-acceptance-checkbox";
import { fetchSchoolCheckoutConfig } from "~/widgets/lib/school-checkout-config";
import { getSourceInfo } from "~/widgets/lib/getSourceInfo";
import { useAuthSnapshot } from "~/widgets/lib/auth-client";
import type { TermsSettings, CustomTerms } from "../../../PurchaseOverlay/useSchoolCheckoutConfig";
import { Notice } from "../../parts";
import { authInit } from "../reschedule/request";
import type { BundleDoc, ManageSchool } from "./types";
import { formatDateTime } from "./bundleManage";
import { displayTimezone } from "../../timezones";
import {
  RedemptionError,
  buyerFromBundle,
  buyerProblems,
  confirmRedemption,
  redemptionBody,
  type Buyer,
  type RedemptionItem,
} from "./redemption";

// The last step of booking with the bundle (v1's basket checkout with the
// bundle as the payment method): what's being booked, who for, the school's
// terms, and "Confirm booking". Nothing is charged; the bundle's spots pay.

export type CheckoutConfig = {
  name?: string;
  timezone?: string;
  requireLoginToBook?: boolean;
  termsSettings?: TermsSettings & { termsAndConditions?: { mandatoryTickBox?: boolean } };
  customTerms?: CustomTerms | null;
  businessCountry?: string;
};

export function useCheckoutConfig(api: string, schoolId: string) {
  return useQuery({
    queryKey: ["booking", "checkout-config", api, schoolId],
    enabled: !!schoolId,
    queryFn: () => fetchSchoolCheckoutConfig<CheckoutConfig>(schoolId, api),
    staleTime: 5 * 60_000,
  });
}

export function RedeemConfirm({
  api,
  school,
  bundle,
  items,
  spots,
  termsHref,
  onSignIn,
  onOpenTerms,
  onPlaced,
  onBack,
}: {
  api: string;
  school: ManageSchool;
  bundle: BundleDoc;
  items: RedemptionItem[];
  spots: number;
  termsHref: string;
  onSignIn: () => void;
  onOpenTerms: (config: CheckoutConfig | undefined) => void;
  onPlaced: (orderId: string) => void;
  /** Back to the step before (the checkout), when there is one. */
  onBack?: () => void;
}) {
  const auth = useAuthSnapshot();
  const config = useCheckoutConfig(api, school._id);
  const [buyer, setBuyer] = useState<Buyer>(() => buyerFromBundle(bundle, auth.user));
  const [accepted, setAccepted] = useState(false);
  const [touched, setTouched] = useState(false);
  const [showProblems, setShowProblems] = useState(false);

  // The account is linked once its owner signs in here (it's the bundle's).
  const linked = buyerFromBundle(bundle, auth.user).userId;
  const order = useMutation({
    mutationFn: () =>
      confirmRedemption(
        api,
        redemptionBody({ items, buyer: { ...buyer, userId: linked }, bundleId: bundle.id, sourceInfo: getSourceInfo() }),
        authInit(auth.token),
      ),
    onSuccess: (orderId) => onPlaced(orderId),
  });

  if (config.isLoading) {
    return (
      <div className="flex justify-center py-12" aria-busy="true">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  const settings = config.data;
  if (settings?.requireLoginToBook && auth.status !== "authenticated") {
    return <CheckoutLoginGate schoolName={settings.name ?? school.name} onLogin={onSignIn} />;
  }

  const mandatory = !!settings?.termsSettings?.termsAndConditions?.mandatoryTickBox;
  const problems = buyerProblems(buyer);
  const zone = (item: RedemptionItem) => displayTimezone(item.timezone, school.timezone);
  const itemError = (id: string) => (order.error instanceof RedemptionError ? order.error.itemMessages.find((m) => m.itemId === id)?.message : undefined);

  const submit = () => {
    setShowProblems(true);
    setTouched(true);
    if (problems.length > 0 || (mandatory && !accepted) || order.isPending) return;
    order.mutate();
  };

  return (
    <div className="space-y-6">
      <section aria-labelledby="bundle-confirm-what" className="space-y-3">
        <h3 id="bundle-confirm-what" className="text-sm font-semibold">
          You’re booking
        </h3>
        <ul className="divide-y rounded-lg border">
          {items.map((item) => (
            <li key={item.id} className="space-y-1 p-3">
              <p className="font-medium">{item.courseName || "Class"}</p>
              {item.sessionDate && <p className="text-sm text-muted-foreground">{formatDateTime(item.sessionDate, zone(item))}</p>}
              <p className="text-sm text-muted-foreground">
                {item.ticketSelections.map((t) => `${t.quantity} × ${t.ticketTypeName}`).join(", ")}
              </p>
              {itemError(item.id) && <p className="text-sm text-red-700">{itemError(item.id)}</p>}
            </li>
          ))}
        </ul>
        <p className="flex items-center gap-2 rounded-lg bg-muted/50 p-3 text-sm">
          <Ticket className="h-4 w-4 shrink-0" aria-hidden />
          <span>
            Covered by <strong>{bundle.name}</strong>: uses {spots} spot{spots !== 1 ? "s" : ""}. Nothing to pay.
          </span>
        </p>
      </section>

      <section aria-labelledby="bundle-confirm-who" className="space-y-3">
        <h3 id="bundle-confirm-who" className="text-sm font-semibold">
          Your details
        </h3>
        <PersonalDetailsForm
          values={{ ...buyer, marketingOptIn: false }}
          onChange={(field, value) => {
            if (field === "firstName" || field === "lastName" || field === "phone") setBuyer((b) => ({ ...b, [field]: String(value) }));
          }}
          emailReadOnly
          emailReadOnlyHint="Bookings made with a bundle are in the bundle holder’s name."
          showPhone
          phoneRequired={false}
          showMarketingOptIn={false}
          businessCountry={settings?.businessCountry}
          disabled={order.isPending}
        />
        {mandatory ? (
          <MandatoryAcceptanceCheckbox
            checked={accepted}
            onCheckedChange={(checked) => {
              setAccepted(checked);
              setTouched(true);
            }}
            onOpenTerms={() => onOpenTerms(settings)}
            touched={touched}
            disabled={order.isPending}
          />
        ) : (
          <p className="text-xs text-muted-foreground">
            By booking you agree to our{" "}
            <a href={termsHref} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
              terms of booking
            </a>
            .
          </p>
        )}
      </section>

      {showProblems && problems.length > 0 && (
        <Notice tone="error">
          <ul className="list-disc space-y-1 pl-4">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Notice>
      )}
      {order.isError && (
        <Notice tone="error">
          {order.error instanceof RedemptionError ? order.error.message : "We couldn’t book that with your bundle. Please try again."}
        </Notice>
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {onBack && (
          <Button variant="outline" onClick={onBack} disabled={order.isPending}>
            Back
          </Button>
        )}
        <Button onClick={submit} disabled={order.isPending}>
          {order.isPending ? (
            <>
              <Spinner className="h-4 w-4" />
              Booking…
            </>
          ) : (
            "Confirm booking"
          )}
        </Button>
      </div>
    </div>
  );
}
